import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterBar } from '@/components/feature/FilterBar';

export default function EventsPage() {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);



  const API_BASE = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/events`);
        if (!res.ok) {
          setEvents([]);
          return;
        }
        const data = await res.json();
        // normalize id for consistency with mocks
        const normalized = data.map((ev: any) => ({ ...ev, id: ev._id || ev.id }));
        setEvents(normalized);
      } catch (err) {
        console.error('Failed to load events', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // derive filter options from fetched events
  const filtered = events;

  const sessionsCount = events.length;
  const tutorsCount = new Set(events.map((e) => (e.tutor || '').toString()).filter(Boolean)).size;
  const freeCount = events.filter((e) => e.free).length;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative bg-slate-900 pt-28 pb-14 overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=dark%20professional%20online%20classroom%20virtual%20learning%20environment%20with%20glowing%20screens%20and%20digital%20interface%20elements%2C%20deep%20navy%20background%2C%20abstract%20technology%20aesthetic%2C%20no%20people%2C%20clean%20minimal%20design&width=1440&height=400&seq=evhero1&orientation=landscape" alt="" className="w-full h-full object-cover object-top opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-slate-900/70"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400 mb-3 block">Live Sessions</span>
            <h1 className="text-4xl font-extrabold text-white mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Upcoming Events</h1>
            <p className="text-slate-400 text-base max-w-xl">Join live revision sessions led by expert tutors. Aligned to AQA, OCR, Edexcel and WJEC syllabuses for GCSE and A-Level.</p>
            <div className="flex flex-wrap gap-6 mt-8">
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>{sessionsCount}</p>
                <p className="text-slate-400 text-xs">Sessions</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>{tutorsCount}</p>
                <p className="text-slate-400 text-xs">Expert Tutors</p>
              </div>
              {/* Free count removed */}
            </div>
          </div>
        </section>

        {/* Filters */}
        <FilterBar count={filtered.length} countLabel="sessions" />

        {/* Events Grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <p className="text-sm text-slate-500 mb-6">{filtered.length} session{filtered.length !== 1 ? 's' : ''} found</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((ev) => {
              const eventDate = ev.date ? new Date(ev.date) : null;
              const day = eventDate ? String(eventDate.getDate()).padStart(2, '0') : '';
              const month = eventDate ? eventDate.toLocaleString('en-GB', { month: 'short' }) : '';
              const totalSpots = typeof ev.spots === 'number' ? ev.spots : (ev.totalSpots ?? null);
              const booked = typeof ev.booked === 'number' ? ev.booked : (ev.booked ?? 0);
              const spotsLeft = totalSpots !== null ? Math.max(0, totalSpots - booked) : (ev.spotsLeft ?? null);
              const progressPercent = totalSpots ? Math.round((booked / totalSpots) * 100) : 0;

              return (
                <div key={ev.id || ev._id} className="bg-white rounded-xl border border-slate-100 p-6 hover:border-emerald-200 transition-all">
                  <div className="flex gap-5">
                    <div className="w-16 h-16 flex-shrink-0 flex flex-col items-center justify-center bg-emerald-700 rounded-xl text-white">
                      <span className="text-2xl font-bold leading-none" style={{ fontFamily: "'Inter', sans-serif" }}>{day}</span>
                      <span className="text-xs font-medium opacity-90">{month}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${((ev.level || ev.category) === 'GCSE') ? 'bg-emerald-100 text-emerald-700' : ((ev.level || ev.category) === 'A-Level') ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>{ev.level || ev.category || '-'}</span>
                        {(ev.board || ev.examBoard) && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ev.board || ev.examBoard}</span>
                        )}
                        {ev.free && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Free</span>}
                      </div>
                      <h3 className="text-slate-800 font-bold text-base mb-1">{ev.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed mb-3 line-clamp-2">{ev.description || ''}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-400 mb-4">
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i>{ev.time || '-'} · {ev.duration ? `${ev.duration} min` : '-'}</span>
                        <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{ev.location || '-'}</span>
                        <span className="flex items-center gap-1"><i className="ri-user-line"></i>{ev.tutor || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="w-32 bg-slate-100 rounded-full h-1.5 mb-1">
                            <div className="bg-emerald-600 h-1.5 rounded-full" style={{ width: `${progressPercent}%` }}></div>
                          </div>
                          <p className="text-xs text-slate-400">{spotsLeft !== null ? `${spotsLeft} spots left` : 'Spots not set'}</p>
                        </div>
                        <Link to={`/events/${ev.id || ev._id}`} className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                          {ev.free ? 'Join Free' : 'Book Now'}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mx-auto mb-4"><i className="ri-calendar-line text-slate-400 text-2xl"></i></div>
              <p className="text-slate-600 font-medium">No sessions match your filters</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
